import { useEffect, useState } from 'react';
import { getTokenDetails } from '@/services/token.service';

interface UseTokenResult {
  tokenData: any | null;
  loading: boolean;
  error: string | null;
  authToken: string | null;
}

/**
 * useToken - Fetches token details and sets authToken/member in localStorage if present.
 * @param token The share/invite token string
 * @returns { tokenData, loading, error, authToken }
 */
export function useToken(token: string | null): UseTokenResult {
  const [tokenData, setTokenData] = useState<any | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!!token);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('No token provided.');
      setLoading(false);
      return;
    }
    setLoading(true);
    getTokenDetails(token)
      .then((data) => {
        setTokenData(data);
        const authToken = data?.authToken;
        const params = data?.token_params;
        if (authToken) {
          sessionStorage.setItem('token', authToken);
        }
        // Set a minimal member object for role guard and auth guard
        if (params) {
          if (params.email) {
            const memberObj = {
              email: params.email,
              roleType: 'token',
            };
            sessionStorage.setItem('member', JSON.stringify(memberObj));
          } else {
            console.warn('[useToken] Missing email or role_id in token_params:', params);
          }
        } else {
          console.warn('[useToken] No token_params found in token response:', data);
        }
        setAuthToken(authToken || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || 'Invalid or expired token.');
        setLoading(false);
      });
  }, [token]);

  return { tokenData, loading, error, authToken };
}
